import React, { useState } from "react";

import { Box, SxProps, TextField, Typography } from "@mui/material";
import Linkify from "linkify-react";
import { makeStyles } from "tss-react/mui";

import { colors } from "@/theme/overrides/colors";

import MessageBubble, { MessageBubbleProps } from "./MessageBubble";
import MessageMeta, { MessageMetaProps } from "./MessageMeta";

interface Props extends MessageBubbleProps, MessageMetaProps {
  body: string;
  deletedAt?: string | null;
  onReply: () => void;
  onDelete?: () => void;
  onEditMessage?: (message: string) => void;
  clientMessage?: string;
}

const useStyles = makeStyles()({
  text: {
    fontSize: 16,
    lineHeight: 1.605,
    color: "#0B1B26",
    textAlign: "left",
    whiteSpace: "pre-wrap",
  },
});

const clientMessageStyle: SxProps = {
  backgroundColor: colors.grayLight400,
  paddingX: 3,
  paddingY: 2,
} as const;

const MessageText: React.FC<Props> = ({
  align,
  body,
  createdAt,
  editedAt,
  deletedAt,
  isSent,
  isRead,
  isDeleted,
  clientMessage,
  onReply,
  onEditMessage,
  onDelete,
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { classes } = useStyles();

  const handleEdit = (value: boolean) => setIsEditing(value);

  return (
    <>
      {clientMessage && (
        <MessageBubble sx={clientMessageStyle} align={align} isDeleted={true}>
          <Typography color={colors.subtitle}>{clientMessage}</Typography>
        </MessageBubble>
      )}
      <MessageBubble
        align={align}
        sx={{
          ...(isEditing && {
            border: 1,
            borderColor: colors.primary,
            width: "600px",
          }),
          marginTop: -1.5,
        }}
        onEdit={handleEdit}
        onReply={onReply}
        onDelete={onDelete}
        isDeleted={isDeleted}
      >
        <Box
          display="flex"
          flexDirection="column"
          textAlign="left"
          paddingX={3}
          paddingY={2}
        >
          {isEditing ? (
            <TextField
              variant="standard"
              multiline
              maxRows={4}
              defaultValue={body}
              InputProps={{ disableUnderline: true }}
              onChange={({ target: { value } }) => {
                if (onEditMessage) onEditMessage(value);
              }}
              fullWidth
            />
          ) : (
            <Typography className={classes.text}>
              <Linkify>
                <Typography sx={{ color: deletedAt && colors.grayDark300 }}>
                  {body}
                </Typography>
              </Linkify>
              <MessageMeta
                createdAt={createdAt}
                isSent={isSent}
                isRead={isRead}
                editedAt={editedAt}
              />
            </Typography>
          )}
        </Box>
      </MessageBubble>
    </>
  );
};

export default MessageText;
